import { get, del, post, put, upload } from '@api/request';

export const fetchDocuments = async (
  { eventService },
  authenticationToken,
  { eventID },
) => {
  const url = `${eventService}/events/pevent/${eventID}/files`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchDescriptionCodefile = async (
  { eventService },
  authenticationToken,
) => {
  const url = `${eventService}/events/codefiles?type=event&name=file%20attachment%20description`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const createDocument = async (
  { eventService },
  authenticationToken,
  { eventID, payload },
) => {
  const url = `${eventService}/events/pevent/${eventID}/files`;
  const { body } = await upload(authenticationToken, url, payload);
  return body;
};

export const deleteDocument = async (
  { eventService },
  authenticationToken,
  { eventID, s3ID, payload },
) => {
  const url = `${eventService}/events/pevent/${eventID}/file/${s3ID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export async function updateDocument(
  { eventService },
  authenticationToken,
  { eventID, payload },
) {
  const url = `${eventService}/events/pevent/${eventID}/files`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export const getDocument = async (
  { eventService },
  authenticationToken,
  { eventID, s3ID },
) => {
  const url = `${eventService}/events/pevent/${eventID}/file/${s3ID}`;

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      if (s3ID.includes('.pdf')) {
        window.open(objectUrl, '_blank');
      } else {
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.href = objectUrl;
        anchor.download = s3ID;
        anchor.click();

        window.URL.revokeObjectURL(objectUrl);
      }
    });
};

export async function fetchEvent(
  { eventService },
  authenticationToken,
  { eventID },
) {
  const url = `${eventService}/events/pevent/${eventID}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchEventTags(
  { eventService },
  authenticationToken,
  { eventID },
) {
  const url = `${eventService}/events/pevent/${eventID}/tags`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchEventObjects(
  { eventService },
  authenticationToken,
  { eventID },
) {
  const url = `${eventService}/events/pevent/${eventID}/objects`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updateEvent(
  { eventService },
  authenticationToken,
  { eventID, payload },
) {
  const url = `${eventService}/events/pevent/${eventID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function deleteEvent(
  { eventService },
  authenticationToken,
  { eventID },
) {
  const url = `${eventService}/events/pevent/${eventID}`;
  await del(authenticationToken, url);
  return { eventID };
}

export async function createEvent(
  { eventService },
  authenticationToken,
  { payload },
) {
  const url = `${eventService}/events/pevents`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function updateEventTagObject(
  { eventService },
  authenticationToken,
  { eventID, payload },
) {
  const url = `${eventService}/events/pevent/${eventID}/tagsobjects`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function activateEvent(
  { eventService },
  authenticationToken,
  { eventID, payload },
) {
  const url = `${eventService}/events/pevent/${eventID}/activate`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function inactivateEvent(
  { eventService },
  authenticationToken,
  { eventID, payload },
) {
  const url = `${eventService}/events/pevent/${eventID}/inactivate`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function fetchEventRevisionHistory(
  { eventService },
  authenticationToken,
  { eventID },
) {
  const url = `${eventService}/events/${eventID}/revisionhistory`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export const uploadEventImages = async (
  { eventService },
  authenticationToken,
  { payload },
) => {
  const url = `${eventService}/events/uploadfileattachments`;
  const { body } = await upload(authenticationToken, url, payload);
  return body;
};

export const attachEventsImage = async (
  { eventService },
  authenticationToken,
  { fileAttachmentID, payload },
) => {
  const url = `${eventService}/events/fileattachments/${fileAttachmentID}/pevents`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
};

export const attachEventsImages = async (
  { eventService },
  authenticationToken,
  payload,
) => {
  const url = `${eventService}/events/linkfileattachments`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
};

export const fetchEventLinkedImages = async (
  { eventService },
  authenticationToken,
  { eventID },
) => {
  const url = `${eventService}/events/pevent/${eventID}/linkedfileattachments`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchEventLinkedImage = async (
  { eventService },
  authenticationToken,
  { fileAttachmentEventID },
) => {
  const url = `${eventService}/events/fileattachmentevents/${fileAttachmentEventID}`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const deleteEventLinkedImage = async (
  { eventService },
  authenticationToken,
  { fileAttachmentEventID },
) => {
  const url = `${eventService}/events/fileattachmentevents/${fileAttachmentEventID}`;
  const { body } = await del(authenticationToken, url);
  return body;
};

export const fetchLinkedEvents = async (
  { eventService },
  authenticationToken,
  { fileAttachmentID },
) => {
  const url = `${eventService}/events/fileattachments/${fileAttachmentID}/pevents`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const updateEventImage = async (
  { eventService },
  authenticationToken,
  { fileAttachmentID, payload },
) => {
  const url = `${eventService}/events/fileattachments/${fileAttachmentID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const deleteEventImage = async (
  { eventService },
  authenticationToken,
  { fileAttachmentID },
) => {
  const url = `${eventService}/events/fileattachments/${fileAttachmentID}`;
  const { body } = await del(authenticationToken, url);
  return body;
};

export async function assoicatePropertyOwnerstoEvents(
  { eventService },
  authenticationToken,
  { pID, payload }
) {
  let url = `${eventService}/events/property/${pID}/associateownerstopropertyevents`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function removePropertyOwnerstoEvents(
  { eventService },
  authenticationToken,
  { pID, payload }
) {
  let url = `${eventService}/events/property/${pID}/removeownersfrompropertyevents`;
  const { body } = await del(authenticationToken, url, payload);
  return body;
}

export const downloadEventImage = async (
  { eventService },
  authenticationToken,
  { fileAttachmentID },
) => {
  const url = `${eventService}/events/fileattachments/${fileAttachmentID}/download`;

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = fileAttachmentID;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};
